import { useCallback, useEffect, useState } from "react";
import {
    getLocalPushStatus,
    pushSupported,
    reconcileBackendSubscription,
    subscribeToPush,
    unsubscribeFromPush,
} from "@/lib/pushClient";

/**
 * React binding for the push client. Exposes the flags a component needs to
 * render a "Activar notificaciones push" toggle + banner, plus the two
 * action verbs.
 *
 * Returns:
 *   {
 *     supported,     // browser has SW + PushManager + Notification
 *     permission,    // "default" | "granted" | "denied" | "unsupported"
 *     subscribed,    // has an active PushSubscription in the browser
 *     loading,       // true while an action is in flight
 *     error,         // last error message from subscribe/unsubscribe
 *     subscribe,     // async — MUST be called from a user gesture
 *     unsubscribe,
 *     refresh,       // manual refresh (rarely needed — hook re-checks on mount)
 *   }
 */
export function usePushSubscription() {
    const [state, setState] = useState({
        supported: pushSupported(),
        permission: typeof Notification !== "undefined" ? Notification.permission : "unsupported",
        subscribed: false,
        loading: true,
        error: null,
    });

    const refresh = useCallback(async () => {
        setState((s) => ({ ...s, loading: true }));
        try {
            const status = await getLocalPushStatus({ withBackendStatus: true });
            // Fix B (25-Sep-2026) — auto-heal desync: if the browser thinks
            // it's subscribed but the backend has 0 rows for this user, we
            // re-post the existing Subscription to /api/push/subscribe. This
            // covers the wipe / migration / first-login-after-deploy case
            // that made all agents' push notifications silently disappear.
            let effectiveSubscribed = status.subscribed;
            if (
                status.supported
                && status.permission === "granted"
                && status.subscribed
                && status.backendSubscriptions === 0
            ) {
                const r = await reconcileBackendSubscription();
                if (r.reconciled) {
                    // The browser sub was just re-registered on the backend;
                    // it's now in sync.
                    effectiveSubscribed = true;
                } else if (r.reason === "no-local-sub") {
                    effectiveSubscribed = false;
                }
            }
            setState({
                supported: status.supported,
                permission: status.permission,
                subscribed: effectiveSubscribed,
                loading: false,
                error: null,
            });
        } catch (e) {
            setState((s) => ({ ...s, loading: false, error: e?.message || String(e) }));
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const subscribe = useCallback(async () => {
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const status = await subscribeToPush();
            setState({
                supported: status.supported,
                permission: status.permission,
                subscribed: status.subscribed,
                loading: false,
                error: null,
            });
            return { ok: true };
        } catch (e) {
            const msg = e?.message || String(e);
            setState((s) => ({ ...s, loading: false, error: msg }));
            return { ok: false, error: msg };
        }
    }, []);

    const unsubscribe = useCallback(async () => {
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            await unsubscribeFromPush();
            await refresh();
            return { ok: true };
        } catch (e) {
            const msg = e?.message || String(e);
            setState((s) => ({ ...s, loading: false, error: msg }));
            return { ok: false, error: msg };
        }
    }, [refresh]);

    return { ...state, subscribe, unsubscribe, refresh };
}
